import { cloneDeep } from 'lodash';
import { PPTSaver as TextLayerPPTSaver } from '@/layers/text-layer';
import { getNewCenterPoint } from '@/utils/layer-kit';
import { pxToInch } from '@/utils/unit-kit';
import { type ILayerProps, type ISize, type ISlide, type IVectorLayerProps } from '@/version-types';
import { checkIsUsingEffect } from './effect';
import { transformToTextLayer } from './layer';
import { LayerCanvasCreator } from './layer-canvas';

export class PPTSaver {
    private layer: ILayerProps;
    private layerData: IVectorLayerProps;

    private isUsingEffect: boolean;

    constructor(
        private identity: string,
        layer: ILayerProps,
    ) {
        this.layer = layer;
        this.layerData = layer.data as IVectorLayerProps;

        this.isUsingEffect = checkIsUsingEffect(this.layerData, {
            includeBlur: true,
        });
    }

    private updateLayerSize(layer: ILayerProps, size: ISize, direction: number) {
        const { width, height, left, top, rotate } = layer;
        const center = getNewCenterPoint(
            {
                width,
                height,
                left,
                top,
                rotate,
            },
            size,
            false,
            direction,
        );

        layer.top = center.y - size.height / 2;
        layer.left = center.x - size.width / 2;
        layer.width = size.width;
        layer.height = size.height;
    }

    public async save(slide: ISlide, externalFonts?: string[]) {
        const layerCanvasCreator = new LayerCanvasCreator(this.identity, this.layer);
        const layerCanvas = await layerCanvasCreator.create({ isPPT: true });

        if (!layerCanvas) return;

        const { width, height, left, top, rotate } = this.layer;
        const options = {
            x: pxToInch(left),
            y: pxToInch(top),
            w: pxToInch(width),
            h: pxToInch(height),
            rotate: rotate > 0 ? rotate : 360 + rotate, // 0 - 360
            data: '',
        };

        if (this.isUsingEffect) {
            const { scaleWidth, scaleHeight, reflectionHeight } = layerCanvasCreator.getEffectLayoutInfluence();
            const layer = cloneDeep(this.layer);

            const reflection = this.layerData.effect.reflection[0];
            const reflectionDistance = reflection ? reflection.y : 0;
            const isReflectionInTop = reflectionDistance < -layer.height;

            const newSize = {
                width: layer.width,
                height: layer.height + scaleHeight,
            };

            // 上/下扩展
            this.updateLayerSize(layer, newSize, isReflectionInTop ? 1 : -1);

            newSize.height = newSize.height + scaleHeight + reflectionHeight;
            this.updateLayerSize(layer, newSize, isReflectionInTop ? -1 : 1);

            //左扩展
            newSize.width = newSize.width + scaleWidth;
            this.updateLayerSize(layer, newSize, -1);

            // 右扩展
            newSize.width = newSize.width + scaleWidth;
            this.updateLayerSize(layer, newSize, 1);

            options.x = pxToInch(layer.left);
            options.y = pxToInch(layer.top);
            options.w = pxToInch(layer.width);
            options.h = pxToInch(layer.height);
        }

        options.data = layerCanvas.toDataURL();
        slide.addImage(options);

        const { textInfo } = this.layerData;
        if (textInfo && textInfo.text) {
            const textLayer = transformToTextLayer(this.layer);
            const saver = new TextLayerPPTSaver(textLayer);
            saver.save(slide, externalFonts);
        }
    }
}
